import { RotateCcw } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette } from '@/constants/theme';
import { setUndoListener, type UndoOffer } from '@/lib/undo';

/**
 * Global Undo snackbar (Wave 48). Mounted once in the root layout,
 * floats above the tab bar. Any screen can surface it via
 * `offerUndo(...)` — a fat-fingered Done / Skip / Delete gets a 5-second
 * escape hatch instead of a hunt through the Completed list.
 *
 * A new offer replaces the current one (last action wins — matches how
 * Gmail / Todoist snackbars behave).
 */

const TOAST_MS = 5000;

export function UndoToastHost() {
  const insets = useSafeAreaInsets();
  const [offer, setOffer] = useState<UndoOffer | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setUndoListener((o) => {
      if (timer.current) clearTimeout(timer.current);
      setBusy(false);
      setOffer(o);
      timer.current = setTimeout(() => setOffer(null), TOAST_MS);
    });
    return () => {
      setUndoListener(null);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!offer) return null;

  const runUndo = async () => {
    if (busy) return;
    setBusy(true);
    if (timer.current) clearTimeout(timer.current);
    try {
      await offer.undo();
      offer.onUndone?.();
    } catch (err) {
      console.warn('undo failed', err);
    } finally {
      setOffer(null);
      setBusy(false);
    }
  };

  return (
    <View
      pointerEvents="box-none"
      // Clears the tab bar AND the center + FAB (which pokes above the
      // bar) — the first placement sat right on top of the FAB.
      style={[styles.wrap, { bottom: Math.max(150, insets.bottom + 134) }]}>
      <View style={styles.toast}>
        <Text style={styles.message} numberOfLines={1}>
          {offer.message}
        </Text>
        <Pressable
          onPress={runUndo}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Undo"
          style={({ pressed }) => [styles.undoBtn, pressed && { opacity: 0.8 }]}>
          <RotateCcw size={13} color={Palette.honey} strokeWidth={2.4} />
          <Text style={styles.undoText}>{busy ? 'Undoing…' : 'Undo'}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 4000,
    // RN-web: keep it above page content that sets its own z-index.
    elevation: 20,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    maxWidth: 420,
    backgroundColor: Palette.ink,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.28,
    shadowRadius: 16,
    elevation: 12,
  },
  message: {
    flexShrink: 1,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.cream,
  },
  undoBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  undoText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.honey,
    letterSpacing: 0.3,
  },
});
